'use es6';

import { Iterable, Record, Set as ImmutableSet } from 'immutable';
import Answer from '../records/Answer';
import AnswerSubmission from '../records/AnswerSubmission';
import Attempt from '../records/Attempt';
import Chapter from '../records/Chapter';
import Exam from '../records/Exam';
import ExamAttempt from '../records/ExamAttempt';
import Link from '../records/Link';
import LearningMaterial from '../records/LearningMaterial';
import Lesson from '../records/Lesson';
import PracticalExercise from '../records/PracticalExercise';
import Professor from '../records/Professor';
import Question from '../records/Question';
import Quiz from '../records/Quiz';
import Video from '../records/Video';
import Track from '../records/Track';
import { ContentResourceType, ContentTypes } from '../constants/ContentConstants';
const keysToAlwaysStringify = ['attemptId', 'certificationId', 'assetId', 'classId', 'id', 'examId', 'questionId', 'contentId', 'quizAttemptId', 'quizId', 'sectionId', 'introVideoId', 'slidesId', 'transcriptId', 'passVideoId'];
const contentToRecordTypeMap = {
  [ContentTypes.QUIZ]: Quiz,
  [ContentTypes.VIDEO]: Video,
  [ContentTypes.PRACTICAL_EXERCISE_FLOW]: PracticalExercise,
  [ContentTypes.LINK]: Link,
  [ContentTypes.EXAM]: Exam,
  [ContentTypes.CHAPTER]: Chapter
};
export function stringifyIds(object) {
  return keysToAlwaysStringify.reduce((obj, key) => {
    if (obj.has(key) && typeof obj.get(key) === 'number') {
      return obj.set(key, obj.get(key).toString());
    }
    return obj;
  }, object);
}
function toAnswerRecord(rawAnswer) {
  return Answer(rawAnswer);
}
function toAnswerSubmissionRecord(rawAnswerSubmission) {
  return AnswerSubmission(rawAnswerSubmission.set('answerIds', ImmutableSet(rawAnswerSubmission.get('answerIds'))));
}
function toQuestionRecord(rawQuestion) {
  return Question(rawQuestion);
}
function toQuizAttemptRecord(rawAttempt) {
  return Attempt(rawAttempt);
}
function toContentRecord(rawContent) {
  const type = rawContent.get('contentType');
  const recordType = contentToRecordTypeMap[type];
  if (!recordType) {
    console.error(`Unhandled content type ${type} could not be mapped`);
    return rawContent;
  }
  return recordType(rawContent);
}
function toLessonRecord(rawLesson) {
  // todo: remove this flattening once the API returns professors at root level
  const allProfessors = rawLesson.get('professors') || rawLesson.get('content').flatMap(thisContent => thisContent.get('professors')).groupBy(professor => professor.get('id')).map(group => group.first()).toList();
  return Lesson(rawLesson.set('professors', allProfessors));
}
function toTrackRecord(rawTrack) {
  return Track(rawTrack);
}
function isValidContent(content) {
  // if the content Map has been parsed into a "Record" it means it's handled by the "toContentRecord" function
  return content instanceof Record;
}
function toContainerRecord(rawResource) {
  switch (rawResource.get('contentResourceType')) {
    case ContentResourceType.MODULE:
    case ContentResourceType.LESSON:
      return toLessonRecord(rawResource);
    case ContentResourceType.TRACK:
      return toTrackRecord(rawResource);
    default:
      return rawResource;
  }
}
function toLearningMaterial(rawLearningMaterial) {
  return LearningMaterial(rawLearningMaterial);
}
export function toExamAttemptRecord(rawExamAttempt) {
  const listQuestionIDs = rawExamAttempt.get('questionIds');
  const nbQuestions = listQuestionIDs && listQuestionIDs.size;
  const newMap = rawExamAttempt.set('countQuestionsInExam', nbQuestions);
  return new ExamAttempt(newMap);
}
export function mapperFunction(key, value) {
  if (Iterable.isKeyed(value)) {
    // Objects mapping
    const rawMap = stringifyIds(value.toMap());
    if (rawMap.has('contentType')) {
      // it's a "Content" object
      return toContentRecord(rawMap);
    }
    if (rawMap.has('contentResourceType')) {
      // it's a MODULE or a COURSE (we don't map courses to specific records atm)
      return toContainerRecord(rawMap);
    }

    // "blueprint-based" approach: we check against specific keys that belong to specific records.
    // todo: a resourceType approach, similar to MODULE and COURSE would be helpful here

    if (rawMap.has('answerSubmissions')) {
      // it's a Quiz Attempt
      return toQuizAttemptRecord(rawMap);
    }
    if (rawMap.has('answerIds')) {
      return toAnswerSubmissionRecord(rawMap);
    }
    if (rawMap.has('questionId') && rawMap.has('text')) {
      return toAnswerRecord(rawMap);
    }
    if (rawMap.has('question') && rawMap.has('answers')) {
      return toQuestionRecord(rawMap);
    }
    if (rawMap.has('attemptStartTime') && rawMap.has('examId')) {
      return toExamAttemptRecord(rawMap);
    }
    if (rawMap.has('contentId') && rawMap.has('descriptions') && rawMap.has('image')) {
      return Professor(rawMap);
    }
    if (rawMap.has('targetUrl') && rawMap.has('type')) {
      return toLearningMaterial(rawMap);
    }
    return rawMap;
  }

  // Lists mapping
  switch (key) {
    case 'content':
      // safety measure to prevent unhandled content types
      return value.toList().filter(isValidContent);
    default:
      return Iterable.isKeyed(value) ? stringifyIds(value.toMap()) : value.toList();
  }
}